import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../../../store';
import type { RBACGroup, RBACUser, RoleMember } from '../../../types';

// The Roles page edits three independent relationships, each staged before one
// Submit. Members and owners are users; ACL nesting is groups. (Separate from the
// Users page's single-dimension roleMembershipSlice — see CONTEXT.md "Pending change".)
export type BucketName = 'members' | 'owners' | 'aclNesting';

type SliceState = {
  members: { pendingAdds: RoleMember[]; pendingDeletes: RoleMember[] };
  owners: { pendingAdds: RBACUser[]; pendingDeletes: RBACUser[] };
  aclNesting: { pendingAdds: RBACGroup[]; pendingDeletes: RBACGroup[] };
};

const emptyBucket = () => ({ pendingAdds: [], pendingDeletes: [] });

const initialState: SliceState = {
  aclNesting: emptyBucket(),
  members: emptyBucket(),
  owners: emptyBucket(),
};

// All staged items share { id, displayName }, so the toggle logic is identical
// across buckets — operate on this minimal shape and let the reducers cast.
type IdItem = { id: string };
type Bucket = { pendingAdds: IdItem[]; pendingDeletes: IdItem[] };
type Payload = PayloadAction<{ bucket: BucketName; item: RBACUser | RBACGroup | RoleMember }>;

const bucketOf = (state: SliceState, name: BucketName): Bucket => state[name] as unknown as Bucket;

export const roleManagementSlice = createSlice({
  initialState,
  name: 'roleManagement',
  reducers: {
    // Cleared on roleId change (routes/roles.tsx), mirroring the Users page.
    clearRoleManagement: () => initialState,

    // Stage an add, unless the item is already pending (compare by id: object
    // identity never matches across immer drafts).
    stageAdd: (state, action: Payload) => {
      const bucket = bucketOf(state, action.payload.bucket);
      if (!bucket.pendingAdds.some((item) => item.id === action.payload.item.id)) {
        bucket.pendingAdds.push(action.payload.item);
      }
    },

    // Toggle a delete. If the id was a pending add, cancel that add instead; if
    // it's already a pending delete, un-stage it; otherwise stage the delete.
    stageDelete: (state, action: Payload) => {
      const bucket = bucketOf(state, action.payload.bucket);
      const { id } = action.payload.item;
      if (bucket.pendingAdds.some((item) => item.id === id)) {
        bucket.pendingAdds = bucket.pendingAdds.filter((item) => item.id !== id);
      } else if (!bucket.pendingDeletes.some((item) => item.id === id)) {
        bucket.pendingDeletes.push(action.payload.item);
      } else {
        bucket.pendingDeletes = bucket.pendingDeletes.filter((item) => item.id !== id);
      }
    },

    unstageAdd: (state, action: Payload) => {
      const bucket = bucketOf(state, action.payload.bucket);
      bucket.pendingAdds = bucket.pendingAdds.filter((item) => item.id !== action.payload.item.id);
    },

    unstageDelete: (state, action: Payload) => {
      const bucket = bucketOf(state, action.payload.bucket);
      bucket.pendingDeletes = bucket.pendingDeletes.filter(
        (item) => item.id !== action.payload.item.id
      );
    },
  },
});

export default roleManagementSlice.reducer;

export const { stageAdd, stageDelete, unstageAdd, unstageDelete, clearRoleManagement } =
  roleManagementSlice.actions;

export const selectMemberAdds = (state: RootState) => state.roleManagement.members.pendingAdds;
export const selectMemberDeletes = (state: RootState) =>
  state.roleManagement.members.pendingDeletes;
export const selectOwnerAdds = (state: RootState) => state.roleManagement.owners.pendingAdds;
export const selectOwnerDeletes = (state: RootState) => state.roleManagement.owners.pendingDeletes;
export const selectAclAdds = (state: RootState) => state.roleManagement.aclNesting.pendingAdds;
export const selectAclDeletes = (state: RootState) =>
  state.roleManagement.aclNesting.pendingDeletes;
